// src/components/admin/SecuritySettings.jsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  FiLock,
  FiMail,
  FiShield,
  FiCheck,
  FiX,
  FiAlertCircle,
  FiInfo,
} from 'react-icons/fi';

function SecuritySettings() {
  const { currentUser, getIdToken } = useAuth();

  // =============================================
  // State for Change Password Modal
  // =============================================
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');

  // =============================================
  // State for Change Email Modal (with current password)
  // =============================================
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [currentEmailPassword, setCurrentEmailPassword] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailError, setEmailError] = useState('');

  // =============================================
  // State for Forgot Password Flow (without current password)
  // =============================================
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
  const [forgotStep, setForgotStep] = useState(1); // 1: request code, 2: verify & reset
  const [verificationId, setVerificationId] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [newForgotPassword, setNewForgotPassword] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotError, setForgotError] = useState('');

  // =============================================
  // State for Forgot Email Flow (without current password)
  // =============================================
  const [showForgotEmailModal, setShowForgotEmailModal] = useState(false);
  const [emailForgotStep, setEmailForgotStep] = useState(1); // 1: request code, 2: verify & change
  const [emailVerificationId, setEmailVerificationId] = useState(null);
  const [emailVerificationCode, setEmailVerificationCode] = useState('');
  const [newEmailAddress, setNewEmailAddress] = useState('');
  const [emailForgotLoading, setEmailForgotLoading] = useState(false);
  const [emailForgotError, setEmailForgotError] = useState('');

  // =============================================
  // Success Message State
  // =============================================
  const [successMessage, setSuccessMessage] = useState('');

  // =============================================
  // Helper Functions
  // =============================================
  const showSuccess = message => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(''), 5000);
  };

  const clearErrors = () => {
    setPasswordError('');
    setEmailError('');
    setForgotError('');
    setEmailForgotError('');
  };

  // Prevent body scroll when modals are open
  useEffect(() => {
    if (
      showPasswordModal ||
      showEmailModal ||
      showForgotPasswordModal ||
      showForgotEmailModal
    ) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [
    showPasswordModal,
    showEmailModal,
    showForgotPasswordModal,
    showForgotEmailModal,
  ]);

  // =============================================
  // 1. Change Password with Current Password
  // =============================================
  const handleChangePassword = async () => {
    clearErrors();

    if (!currentPassword) {
      setPasswordError('Please enter your current password');
      return;
    }

    if (!newPassword) {
      setPasswordError('Please enter a new password');
      return;
    }

    if (newPassword.length < 6) {
      setPasswordError('Password must be at least 6 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    setPasswordLoading(true);
    try {
      const token = await getIdToken();

      const response = await fetch('/api/verify-admin', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          action: 'changePassword',
          currentPassword,
          newPassword,
        }),
      });

      const data = await response.json();

      if (data.success) {
        showSuccess('✅ Password changed successfully!');
        setShowPasswordModal(false);
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        setPasswordError(data.error || 'Failed to change password');
      }
    } catch (error) {
      console.error('Password change error:', error);
      setPasswordError('Network error. Please try again.');
    } finally {
      setPasswordLoading(false);
    }
  };

  // =============================================
  // 2. Change Email with Current Password
  // =============================================
  const handleChangeEmail = async () => {
    clearErrors();

    if (!currentEmailPassword) {
      setEmailError('Please enter your current password');
      return;
    }

    if (!newEmail || !newEmail.includes('@')) {
      setEmailError('Please enter a valid email address');
      return;
    }

    setEmailLoading(true);
    try {
      const token = await getIdToken();

      const response = await fetch('/api/verify-admin', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          action: 'changeEmail',
          currentPassword: currentEmailPassword,
          newEmail,
        }),
      });

      const data = await response.json();

      if (data.success) {
        showSuccess(
          '✅ Email changed successfully! Please login with your new email.',
        );
        setTimeout(() => {
          window.location.href = '/wdalogin';
        }, 2000);
      } else {
        setEmailError(data.error || 'Failed to change email');
      }
    } catch (error) {
      console.error('Email change error:', error);
      setEmailError('Network error. Please try again.');
    } finally {
      setEmailLoading(false);
    }
  };

  // =============================================
  // 3. Forgot Password - Request Verification Code
  // =============================================
  const handleRequestPasswordReset = async () => {
    clearErrors();
    setForgotLoading(true);
    try {
      const token = await getIdToken();

      const response = await fetch('/api/verify-admin', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          action: 'requestVerification',
          type: 'password',
        }),
      });

      const data = await response.json();

      if (data.success) {
        setVerificationId(data.verificationId);
        setForgotStep(2);
        if (data.devCode) {
          showSuccess(
            `📧 DEV MODE: Your verification code is: ${data.devCode}`,
          );
        } else {
          showSuccess(
            '📧 Verification code sent to your email! Check your inbox or spam folder.',
          );
        }
      } else {
        setForgotError(data.error || 'Failed to send verification code');
      }
    } catch (error) {
      console.error('Verification request error:', error);
      setForgotError('Network error. Please try again.');
    } finally {
      setForgotLoading(false);
    }
  };

  // =============================================
  // 3. Forgot Password - Verify Code & Reset Password
  // =============================================
  const handleVerifyAndResetPassword = async () => {
    clearErrors();

    if (!verificationCode || verificationCode.length !== 6) {
      setForgotError('Please enter the 6-digit verification code');
      return;
    }

    if (!newForgotPassword || newForgotPassword.length < 6) {
      setForgotError('Password must be at least 6 characters');
      return;
    }

    setForgotLoading(true);
    try {
      const token = await getIdToken();

      const response = await fetch('/api/verify-admin', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          action: 'verifyAndUpdate',
          verificationId,
          verificationCode,
          newValue: newForgotPassword,
          updateType: 'password',
        }),
      });

      const data = await response.json();

      if (data.success) {
        showSuccess(
          '✅ Password reset successfully! Please login with your new password.',
        );
        setTimeout(() => {
          window.location.href = '/wdalogin';
        }, 2000);
      } else {
        setForgotError(data.error || 'Failed to reset password');
      }
    } catch (error) {
      console.error('Password reset error:', error);
      setForgotError('Network error. Please try again.');
    } finally {
      setForgotLoading(false);
    }
  };

  // =============================================
  // 4. Forgot Email - Request Verification Code
  // =============================================
  const handleRequestEmailChange = async () => {
    clearErrors();

    if (!newEmailAddress || !newEmailAddress.includes('@')) {
      setEmailForgotError('Please enter a valid email address');
      return;
    }

    setEmailForgotLoading(true);
    try {
      const token = await getIdToken();

      const response = await fetch('/api/verify-admin', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          action: 'requestVerification',
          type: 'email',
          newEmail: newEmailAddress,
        }),
      });

      const data = await response.json();

      if (data.success) {
        setEmailVerificationId(data.verificationId);
        setEmailForgotStep(2);
        if (data.devCode) {
          showSuccess(
            `📧 DEV MODE: Your verification code is: ${data.devCode}`,
          );
        } else {
          showSuccess(
            `📧 Verification code sent to ${newEmailAddress}! Check your inbox or spam folder.`,
          );
        }
      } else {
        setEmailForgotError(data.error || 'Failed to send verification code');
      }
    } catch (error) {
      console.error('Email verification request error:', error);
      setEmailForgotError('Network error. Please try again.');
    } finally {
      setEmailForgotLoading(false);
    }
  };

  // =============================================
  // 4. Forgot Email - Verify Code & Change Email
  // =============================================
  const handleVerifyAndChangeEmail = async () => {
    clearErrors();

    if (!emailVerificationCode || emailVerificationCode.length !== 6) {
      setEmailForgotError('Please enter the 6-digit verification code');
      return;
    }

    setEmailForgotLoading(true);
    try {
      const token = await getIdToken();

      const response = await fetch('/api/verify-admin', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          action: 'verifyAndUpdate',
          verificationId: emailVerificationId,
          verificationCode: emailVerificationCode,
          newValue: newEmailAddress,
          updateType: 'email',
        }),
      });

      const data = await response.json();

      if (data.success) {
        showSuccess(
          '✅ Email changed successfully! Please login with your new email.',
        );
        setTimeout(() => {
          window.location.href = '/wdalogin';
        }, 2000);
      } else {
        setEmailForgotError(data.error || 'Failed to change email');
      }
    } catch (error) {
      console.error('Email change error:', error);
      setEmailForgotError('Network error. Please try again.');
    } finally {
      setEmailForgotLoading(false);
    }
  };

  // Reset forgot password state when modal closes
  const resetForgotPasswordState = () => {
    setForgotStep(1);
    setVerificationId(null);
    setVerificationCode('');
    setNewForgotPassword('');
    setForgotError('');
  };

  // Reset forgot email state when modal closes
  const resetForgotEmailState = () => {
    setEmailForgotStep(1);
    setEmailVerificationId(null);
    setEmailVerificationCode('');
    setNewEmailAddress('');
    setEmailForgotError('');
  };

  return (
    <div className="flex-1">
      <div
        className="rounded-2xl p-6 border"
        style={{ backgroundColor: 'var(--bg)', borderColor: 'var(--border)' }}
      >
        <h2
          className="text-lg font-semibold mb-4"
          style={{ color: 'var(--text)' }}
        >
          Security Settings
        </h2>

        {/* Success Message */}
        {successMessage && (
          <div className="mb-4 p-3 rounded-lg flex items-center gap-2 bg-green-500/10 text-green-500 border border-green-500/20 animate-fade-in">
            <FiCheck className="w-4 h-4 flex-shrink-0" />
            <span className="text-sm">{successMessage}</span>
          </div>
        )}

        <div className="space-y-4">
          {/* ============================================= */}
          {/* 1. Change Password Section */}
          {/* ============================================= */}
          <div
            className="p-4 rounded-xl transition-all hover:scale-[1.02] duration-300"
            style={{ backgroundColor: 'var(--border)' }}
          >
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <FiLock
                    className="w-4 h-4"
                    style={{ color: 'var(--text)' }}
                  />
                  <h3
                    className="font-semibold"
                    style={{ color: 'var(--text)' }}
                  >
                    Change Password
                  </h3>
                </div>
                <p className="text-xs" style={{ color: 'var(--muted)' }}>
                  Update your password to keep your account secure
                </p>
              </div>
              <button
                onClick={() => setShowPasswordModal(true)}
                className="px-4 py-2 rounded-lg text-sm font-medium transition-all hover:opacity-80 hover:scale-105"
                style={{ backgroundColor: 'var(--text)', color: 'var(--bg)' }}
              >
                Change Password
              </button>
            </div>
          </div>

          {/* ============================================= */}
          {/* 2. Change Email Section (with current password) */}
          {/* ============================================= */}
          <div
            className="p-4 rounded-xl transition-all hover:scale-[1.02] duration-300"
            style={{ backgroundColor: 'var(--border)' }}
          >
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <FiMail
                    className="w-4 h-4"
                    style={{ color: 'var(--text)' }}
                  />
                  <h3
                    className="font-semibold"
                    style={{ color: 'var(--text)' }}
                  >
                    Change Email Address
                  </h3>
                </div>
                <p className="text-xs" style={{ color: 'var(--muted)' }}>
                  Current: {currentUser?.email}
                </p>
              </div>
              <button
                onClick={() => setShowEmailModal(true)}
                className="px-4 py-2 rounded-lg text-sm font-medium transition-all hover:opacity-80 hover:scale-105"
                style={{ backgroundColor: 'var(--text)', color: 'var(--bg)' }}
              >
                Change Email
              </button>
            </div>
          </div>

          {/* ============================================= */}
          {/* 3. Forgot Password Section (without current password) */}
          {/* ============================================= */}
          <div
            className="p-4 rounded-xl transition-all hover:scale-[1.02] duration-300"
            style={{ backgroundColor: 'var(--border)' }}
          >
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <FiShield
                    className="w-4 h-4"
                    style={{ color: 'var(--text)' }}
                  />
                  <h3
                    className="font-semibold"
                    style={{ color: 'var(--text)' }}
                  >
                    Forgot Password?
                  </h3>
                </div>
                <p className="text-xs" style={{ color: 'var(--muted)' }}>
                  Reset your password without current password
                </p>
              </div>
              <button
                onClick={() => setShowForgotPasswordModal(true)}
                className="px-4 py-2 rounded-lg text-sm font-medium transition-all hover:opacity-80 hover:scale-105"
                style={{
                  border: '1px solid var(--border)',
                  color: 'var(--text)',
                }}
              >
                Reset Password
              </button>
            </div>
          </div>

          {/* ============================================= */}
          {/* 4. Forgot Email Section (without current password) */}
          {/* ============================================= */}
          <div
            className="p-4 rounded-xl transition-all hover:scale-[1.02] duration-300"
            style={{ backgroundColor: 'var(--border)' }}
          >
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <FiMail
                    className="w-4 h-4"
                    style={{ color: 'var(--text)' }}
                  />
                  <h3
                    className="font-semibold"
                    style={{ color: 'var(--text)' }}
                  >
                    Can't access your email?
                  </h3>
                </div>
                <p className="text-xs" style={{ color: 'var(--muted)' }}>
                  Change email address without current password
                </p>
              </div>
              <button
                onClick={() => setShowForgotEmailModal(true)}
                className="px-4 py-2 rounded-lg text-sm font-medium transition-all hover:opacity-80 hover:scale-105"
                style={{
                  border: '1px solid var(--border)',
                  color: 'var(--text)',
                }}
              >
                Change Email
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ============================================= */}
      {/* MODAL 1: Change Password Modal */}
      {/* ============================================= */}
      {showPasswordModal && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.8)' }}
        >
          <div
            className="relative max-w-md w-full rounded-2xl shadow-2xl overflow-hidden animate-fade-in-up"
            style={{
              backgroundColor: 'var(--bg)',
              border: '1px solid var(--border)',
            }}
          >
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3
                  className="text-xl font-bold"
                  style={{ color: 'var(--text)' }}
                >
                  Change Password
                </h3>
                <button
                  onClick={() => {
                    setShowPasswordModal(false);
                    setCurrentPassword('');
                    setNewPassword('');
                    setConfirmPassword('');
                    setPasswordError('');
                  }}
                  className="p-1 rounded-full hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
                >
                  <FiX className="w-5 h-5" style={{ color: 'var(--muted)' }} />
                </button>
              </div>

              {passwordError && (
                <div className="mb-4 p-3 rounded-lg flex items-center gap-2 bg-red-500/10 text-red-500 border border-red-500/20">
                  <FiAlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span className="text-sm">{passwordError}</span>
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label
                    className="block text-sm font-medium mb-2"
                    style={{ color: 'var(--text)' }}
                  >
                    Current Password
                  </label>
                  <input
                    type="password"
                    value={currentPassword}
                    onChange={e => setCurrentPassword(e.target.value)}
                    className="w-full px-4 py-2 rounded-xl outline-none transition-all focus:ring-1"
                    style={{
                      backgroundColor: 'var(--border)',
                      color: 'var(--text)',
                    }}
                    placeholder="Enter current password"
                    onKeyPress={e =>
                      e.key === 'Enter' && handleChangePassword()
                    }
                  />
                </div>

                <div>
                  <label
                    className="block text-sm font-medium mb-2"
                    style={{ color: 'var(--text)' }}
                  >
                    New Password
                  </label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={e => setNewPassword(e.target.value)}
                    className="w-full px-4 py-2 rounded-xl outline-none transition-all focus:ring-1"
                    style={{
                      backgroundColor: 'var(--border)',
                      color: 'var(--text)',
                    }}
                    placeholder="Enter new password (min 6 characters)"
                    onKeyPress={e =>
                      e.key === 'Enter' && handleChangePassword()
                    }
                  />
                </div>

                <div>
                  <label
                    className="block text-sm font-medium mb-2"
                    style={{ color: 'var(--text)' }}
                  >
                    Confirm New Password
                  </label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                    className="w-full px-4 py-2 rounded-xl outline-none transition-all focus:ring-1"
                    style={{
                      backgroundColor: 'var(--border)',
                      color: 'var(--text)',
                    }}
                    placeholder="Confirm new password"
                    onKeyPress={e =>
                      e.key === 'Enter' && handleChangePassword()
                    }
                  />
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    onClick={() => {
                      setShowPasswordModal(false);
                      setCurrentPassword('');
                      setNewPassword('');
                      setConfirmPassword('');
                      setPasswordError('');
                    }}
                    className="flex-1 px-4 py-2 rounded-xl transition-all hover:opacity-80"
                    style={{
                      border: '1px solid var(--border)',
                      color: 'var(--text)',
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleChangePassword}
                    disabled={passwordLoading}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-xl transition-all hover:opacity-80 disabled:opacity-50"
                    style={{
                      backgroundColor: 'var(--text)',
                      color: 'var(--bg)',
                    }}
                  >
                    {passwordLoading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Changing...
                      </>
                    ) : (
                      'Change Password'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ============================================= */}
      {/* MODAL 2: Change Email Modal (with current password) */}
      {/* ============================================= */}
      {showEmailModal && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.8)' }}
        >
          <div
            className="relative max-w-md w-full rounded-2xl shadow-2xl overflow-hidden animate-fade-in-up"
            style={{
              backgroundColor: 'var(--bg)',
              border: '1px solid var(--border)',
            }}
          >
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3
                  className="text-xl font-bold"
                  style={{ color: 'var(--text)' }}
                >
                  Change Email Address
                </h3>
                <button
                  onClick={() => {
                    setShowEmailModal(false);
                    setCurrentEmailPassword('');
                    setNewEmail('');
                    setEmailError('');
                  }}
                  className="p-1 rounded-full hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
                >
                  <FiX className="w-5 h-5" style={{ color: 'var(--muted)' }} />
                </button>
              </div>

              {emailError && (
                <div className="mb-4 p-3 rounded-lg flex items-center gap-2 bg-red-500/10 text-red-500 border border-red-500/20">
                  <FiAlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span className="text-sm">{emailError}</span>
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label
                    className="block text-sm font-medium mb-2"
                    style={{ color: 'var(--text)' }}
                  >
                    Current Password
                  </label>
                  <input
                    type="password"
                    value={currentEmailPassword}
                    onChange={e => setCurrentEmailPassword(e.target.value)}
                    className="w-full px-4 py-2 rounded-xl outline-none transition-all focus:ring-1"
                    style={{
                      backgroundColor: 'var(--border)',
                      color: 'var(--text)',
                    }}
                    placeholder="Enter your current password"
                    onKeyPress={e => e.key === 'Enter' && handleChangeEmail()}
                  />
                </div>

                <div>
                  <label
                    className="block text-sm font-medium mb-2"
                    style={{ color: 'var(--text)' }}
                  >
                    New Email Address
                  </label>
                  <input
                    type="email"
                    value={newEmail}
                    onChange={e => setNewEmail(e.target.value)}
                    className="w-full px-4 py-2 rounded-xl outline-none transition-all focus:ring-1"
                    style={{
                      backgroundColor: 'var(--border)',
                      color: 'var(--text)',
                    }}
                    placeholder="Enter new email address"
                    onKeyPress={e => e.key === 'Enter' && handleChangeEmail()}
                  />
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    onClick={() => {
                      setShowEmailModal(false);
                      setCurrentEmailPassword('');
                      setNewEmail('');
                      setEmailError('');
                    }}
                    className="flex-1 px-4 py-2 rounded-xl transition-all hover:opacity-80"
                    style={{
                      border: '1px solid var(--border)',
                      color: 'var(--text)',
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleChangeEmail}
                    disabled={emailLoading}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-xl transition-all hover:opacity-80 disabled:opacity-50"
                    style={{
                      backgroundColor: 'var(--text)',
                      color: 'var(--bg)',
                    }}
                  >
                    {emailLoading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Changing...
                      </>
                    ) : (
                      'Change Email'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ============================================= */}
      {/* MODAL 3: Forgot Password Modal (2-step flow) */}
      {/* ============================================= */}
      {showForgotPasswordModal && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.8)' }}
        >
          <div
            className="relative max-w-md w-full rounded-2xl shadow-2xl overflow-hidden animate-fade-in-up"
            style={{
              backgroundColor: 'var(--bg)',
              border: '1px solid var(--border)',
            }}
          >
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3
                  className="text-xl font-bold"
                  style={{ color: 'var(--text)' }}
                >
                  {forgotStep === 1 ? 'Reset Password' : 'Verify & Reset'}
                </h3>
                <button
                  onClick={() => {
                    setShowForgotPasswordModal(false);
                    resetForgotPasswordState();
                  }}
                  className="p-1 rounded-full hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
                >
                  <FiX className="w-5 h-5" style={{ color: 'var(--muted)' }} />
                </button>
              </div>

              {forgotError && (
                <div className="mb-4 p-3 rounded-lg flex items-center gap-2 bg-red-500/10 text-red-500 border border-red-500/20">
                  <FiAlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span className="text-sm">{forgotError}</span>
                </div>
              )}

              {forgotStep === 1 ? (
                <div className="space-y-4">
                  <div className="p-3 rounded-lg bg-blue-500/10 text-blue-400 text-sm flex items-start gap-2">
                    <FiInfo className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <p>
                      A 6-digit verification code will be sent to your
                      registered email address.
                    </p>
                  </div>
                  <div className="flex gap-3 pt-2">
                    <button
                      onClick={() => {
                        setShowForgotPasswordModal(false);
                        resetForgotPasswordState();
                      }}
                      className="flex-1 px-4 py-2 rounded-xl transition-all hover:opacity-80"
                      style={{
                        border: '1px solid var(--border)',
                        color: 'var(--text)',
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleRequestPasswordReset}
                      disabled={forgotLoading}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-xl transition-all hover:opacity-80 disabled:opacity-50"
                      style={{
                        backgroundColor: 'var(--text)',
                        color: 'var(--bg)',
                      }}
                    >
                      {forgotLoading ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          Sending...
                        </>
                      ) : (
                        'Send Code'
                      )}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label
                      className="block text-sm font-medium mb-2"
                      style={{ color: 'var(--text)' }}
                    >
                      Verification Code
                    </label>
                    <input
                      type="text"
                      value={verificationCode}
                      onChange={e => setVerificationCode(e.target.value)}
                      className="w-full px-4 py-2 rounded-xl outline-none transition-all text-center text-2xl tracking-wider font-mono focus:ring-1"
                      style={{
                        backgroundColor: 'var(--border)',
                        color: 'var(--text)',
                      }}
                      placeholder="000000"
                      maxLength={6}
                      onKeyPress={e =>
                        e.key === 'Enter' && handleVerifyAndResetPassword()
                      }
                    />
                  </div>

                  <div>
                    <label
                      className="block text-sm font-medium mb-2"
                      style={{ color: 'var(--text)' }}
                    >
                      New Password
                    </label>
                    <input
                      type="password"
                      value={newForgotPassword}
                      onChange={e => setNewForgotPassword(e.target.value)}
                      className="w-full px-4 py-2 rounded-xl outline-none transition-all focus:ring-1"
                      style={{
                        backgroundColor: 'var(--border)',
                        color: 'var(--text)',
                      }}
                      placeholder="Enter new password (min 6 characters)"
                      onKeyPress={e =>
                        e.key === 'Enter' && handleVerifyAndResetPassword()
                      }
                    />
                  </div>

                  <div className="flex gap-3 pt-2">
                    <button
                      onClick={() => setForgotStep(1)}
                      className="flex-1 px-4 py-2 rounded-xl transition-all hover:opacity-80"
                      style={{
                        border: '1px solid var(--border)',
                        color: 'var(--text)',
                      }}
                    >
                      Back
                    </button>
                    <button
                      onClick={handleVerifyAndResetPassword}
                      disabled={forgotLoading}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-xl transition-all hover:opacity-80 disabled:opacity-50"
                      style={{
                        backgroundColor: 'var(--text)',
                        color: 'var(--bg)',
                      }}
                    >
                      {forgotLoading ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          Resetting...
                        </>
                      ) : (
                        'Reset Password'
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ============================================= */}
      {/* MODAL 4: Forgot Email Modal (2-step flow) */}
      {/* ============================================= */}
      {showForgotEmailModal && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.8)' }}
        >
          <div
            className="relative max-w-md w-full rounded-2xl shadow-2xl overflow-hidden animate-fade-in-up"
            style={{
              backgroundColor: 'var(--bg)',
              border: '1px solid var(--border)',
            }}
          >
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3
                  className="text-xl font-bold"
                  style={{ color: 'var(--text)' }}
                >
                  {emailForgotStep === 1
                    ? 'Change Email'
                    : 'Verify & Change Email'}
                </h3>
                <button
                  onClick={() => {
                    setShowForgotEmailModal(false);
                    resetForgotEmailState();
                  }}
                  className="p-1 rounded-full hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
                >
                  <FiX className="w-5 h-5" style={{ color: 'var(--muted)' }} />
                </button>
              </div>

              {emailForgotError && (
                <div className="mb-4 p-3 rounded-lg flex items-center gap-2 bg-red-500/10 text-red-500 border border-red-500/20">
                  <FiAlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span className="text-sm">{emailForgotError}</span>
                </div>
              )}

              {emailForgotStep === 1 ? (
                <div className="space-y-4">
                  <div>
                    <label
                      className="block text-sm font-medium mb-2"
                      style={{ color: 'var(--text)' }}
                    >
                      New Email Address
                    </label>
                    <input
                      type="email"
                      value={newEmailAddress}
                      onChange={e => setNewEmailAddress(e.target.value)}
                      className="w-full px-4 py-2 rounded-xl outline-none transition-all focus:ring-1"
                      style={{
                        backgroundColor: 'var(--border)',
                        color: 'var(--text)',
                      }}
                      placeholder="Enter your new email address"
                      onKeyPress={e =>
                        e.key === 'Enter' && handleRequestEmailChange()
                      }
                    />
                  </div>

                  <div className="p-3 rounded-lg bg-blue-500/10 text-blue-400 text-sm flex items-start gap-2">
                    <FiInfo className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <p>
                      A 6-digit verification code will be sent to your new email
                      address.
                    </p>
                  </div>

                  <div className="flex gap-3 pt-2">
                    <button
                      onClick={() => {
                        setShowForgotEmailModal(false);
                        resetForgotEmailState();
                      }}
                      className="flex-1 px-4 py-2 rounded-xl transition-all hover:opacity-80"
                      style={{
                        border: '1px solid var(--border)',
                        color: 'var(--text)',
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleRequestEmailChange}
                      disabled={emailForgotLoading}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-xl transition-all hover:opacity-80 disabled:opacity-50"
                      style={{
                        backgroundColor: 'var(--text)',
                        color: 'var(--bg)',
                      }}
                    >
                      {emailForgotLoading ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          Sending...
                        </>
                      ) : (
                        'Send Code'
                      )}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label
                      className="block text-sm font-medium mb-2"
                      style={{ color: 'var(--text)' }}
                    >
                      Verification Code
                    </label>
                    <input
                      type="text"
                      value={emailVerificationCode}
                      onChange={e => setEmailVerificationCode(e.target.value)}
                      className="w-full px-4 py-2 rounded-xl outline-none transition-all text-center text-2xl tracking-wider font-mono focus:ring-1"
                      style={{
                        backgroundColor: 'var(--border)',
                        color: 'var(--text)',
                      }}
                      placeholder="000000"
                      maxLength={6}
                      onKeyPress={e =>
                        e.key === 'Enter' && handleVerifyAndChangeEmail()
                      }
                    />
                  </div>

                  <div className="flex gap-3 pt-2">
                    <button
                      onClick={() => setEmailForgotStep(1)}
                      className="flex-1 px-4 py-2 rounded-xl transition-all hover:opacity-80"
                      style={{
                        border: '1px solid var(--border)',
                        color: 'var(--text)',
                      }}
                    >
                      Back
                    </button>
                    <button
                      onClick={handleVerifyAndChangeEmail}
                      disabled={emailForgotLoading}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-xl transition-all hover:opacity-80 disabled:opacity-50"
                      style={{
                        backgroundColor: 'var(--text)',
                        color: 'var(--bg)',
                      }}
                    >
                      {emailForgotLoading ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          Changing...
                        </>
                      ) : (
                        'Change Email'
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SecuritySettings;
